import React, { Component } from 'react'
import { TouchableOpacity } from 'react-native'
import { connect } from 'react-redux'

import { getSelectedStore } from 'x/redux/selectors'

import { ISelectedStoreMap, IWarehousesDetail, IWarehousePickerOnPickParams, IWarehousePickerOnSelectParams } from 'x/types'
import * as util from 'x/utils/util'

import _ from 'lodash'
import { List } from 'immutable'
import VStack from '../VStack'
import XText from '../XText'
import HStack from '../HStack'
import XIcon from '../XIcon'
import XOverlay from '../XOverlay'
import XCustomHeader from '../XCustomHeader'
import XContent from '../XContent'
import ForwardIcon from '../ForwardIcon'
import XButton from '../XButton'

interface IWarehousePickerProps {
  selectedStore?: ISelectedStoreMap

  // onPick?: (warehouse: IWarehousesDetail) => void
  title?: string
}

interface IWarehousePickerState {
  isInitialized: boolean
  isVisible: boolean
  mode: 'pick' | 'select'

  warehouses: IWarehousesDetail[]
  availableWharehouseIds: number[]

  onPick?: (whId: number) => void
  onSelect?: (whIds: number[]) => void

  selectedWarehouseId: {
    [whId: number]: boolean
  }
}

export class WarehousePickerBase extends Component<IWarehousePickerProps, IWarehousePickerState> {
  static displayName = 'WarehousePicker'

  inProcess?: boolean

  constructor(props) {
    super(props)

    this.state = {
      isInitialized: false,
      isVisible: false,
      warehouses: [],
      availableWharehouseIds: [],
      mode: 'pick',

      selectedWarehouseId: {},
    }
  }

  async componentDidMount(): Promise<void> {
    await this._setupWarehouses()
    await util.delay(100)
    this.setState({ isInitialized: true })
  }

  _setupWarehouses = async () => {
    const { selectedStore } = this.props

    const warehousesList = selectedStore.get('warehouses') || List([])
    const warehouses: IWarehousesDetail[] = warehousesList.toJS()

    await util.setStatePromise(this, { warehouses })
  }

  // openPicker = (availableWharehouseIds?: number[]) => {
  //   this.setState({ isVisible: true, availableWharehouseIds })
  // }

  openPicker = (params: IWarehousePickerOnPickParams) => {
    // console.log('openPicker availableWharehouseIds => ', availableWharehouseIds)
    this.setState({
      mode: 'pick',
      isVisible: true,
      availableWharehouseIds: params.availableWharehouseIds || [],
      onPick: params.onPick,
    })
  }

  openSelector = (params: IWarehousePickerOnSelectParams) => {
    // console.log('openPicker availableWharehouseIds => ', availableWharehouseIds)

    const selectedWarehouseId: { [whId: number]: boolean } = {}
    params.initialSelectedWhIds?.forEach((whId) => {
      selectedWarehouseId[whId] = true
    })

    this.setState({
      mode: 'select',
      isVisible: true,
      availableWharehouseIds: params.availableWharehouseIds || [],
      selectedWarehouseId,
      onSelect: params.onSelect,
    })
  }

  closePicker = () => {
    this.setState({ isVisible: false, onPick: null, mode: 'pick', selectedWarehouseId: {} })
  }

  _toggleSelectAll = () => {
    if (this.inProcess) {
      return
    }
    this.inProcess = true

    const { warehouses, selectedWarehouseId } = this.state

    const allSelected = warehouses.every((wh) => selectedWarehouseId[wh.id] === true)
    const newSelectedWarehouseId: { [whId: number]: boolean } = {}

    if (!allSelected) {
      warehouses.forEach((wh) => {
        newSelectedWarehouseId[wh.id] = true
      })
    }

    this.setState({ selectedWarehouseId: newSelectedWarehouseId })

    this.inProcess = false
  }

  _handlePickWarehouse = (warehouse: IWarehousesDetail) => {
    if (this.inProcess) {
      return
    }
    this.inProcess = true

    const { onPick, mode, selectedWarehouseId } = this.state

    // select mode
    if (mode === 'select') {
      const newSelectedWarehouseId = { ...selectedWarehouseId }
      if (newSelectedWarehouseId[warehouse.id] === true) {
        delete newSelectedWarehouseId[warehouse.id]
      } else {
        newSelectedWarehouseId[warehouse.id] = true
      }
      this.setState({ selectedWarehouseId: newSelectedWarehouseId })

      this.inProcess = false
      return
    }

    onPick?.(warehouse.id)
    this.closePicker()

    this.inProcess = false
  }

  _handleSubmitSelect = () => {
    if (this.inProcess) {
      return
    }
    this.inProcess = true

    const { onSelect, selectedWarehouseId } = this.state

    const selectedWhIds = Object.keys(selectedWarehouseId)
      .map((whIdStr) => parseInt(whIdStr))
      .filter((whId) => selectedWarehouseId[whId] === true)

    onSelect?.(selectedWhIds)
    this.closePicker()

    this.inProcess = false
  }

  _renderWarehouseItem = (warehouse: IWarehousesDetail) => {
    const { availableWharehouseIds, mode, selectedWarehouseId = {} } = this.state

    const isAvailable =
      _.isNil(availableWharehouseIds) || availableWharehouseIds.length === 0 || availableWharehouseIds.includes(warehouse.id)
    if (!isAvailable) {
      return null
    }

    const isSelectMode = mode === 'select'
    const checked = selectedWarehouseId[warehouse.id] === true

    return (
      <TouchableOpacity
        key={`warehouse-item-${warehouse.id}`}
        onPress={() => {
          this._handlePickWarehouse(warehouse)
        }}
      >
        <HStack w='full' alignItems='center' p='2' bg='white' borderRadius='md' borderWidth={1} borderColor='gray.200'>
          {isSelectMode && (
            <VStack w='34px' alignItems='center' justifyContent='center'>
              <XIcon variant='primary' family={checked ? 'Ionicons' : 'FontAwesome'} name={checked ? 'checkmark-circle' : 'circle-o'} />
            </VStack>
          )}

          <XText flex={1} fontSize='md'>
            {warehouse.name}
          </XText>

          {isSelectMode ? null : <ForwardIcon />}
        </HStack>
      </TouchableOpacity>
    )
  }

  _renderSelectAllItem = () => {
    const { availableWharehouseIds, mode, selectedWarehouseId = {} } = this.state
    const isSelectMode = mode === 'select'
    if (!isSelectMode) {
      return null
    }

    const isAvailable = _.isNil(availableWharehouseIds) || availableWharehouseIds.length === 0
    if (!isAvailable) {
      return null
    }

    const isSelectedAll = this.state.warehouses.every((wh) => selectedWarehouseId[wh.id] === true)

    return (
      <TouchableOpacity key={`warehouse-item-select-all`} onPress={this._toggleSelectAll}>
        <HStack w='full' alignItems='center' p='2' bg='white' borderRadius='md' borderWidth={1} borderColor='gray.200'>
          <VStack w='34px' alignItems='center' justifyContent='center'>
            <XIcon
              variant='primary'
              family={isSelectedAll ? 'Ionicons' : 'FontAwesome'}
              name={isSelectedAll ? 'checkmark-circle' : 'circle-o'}
            />
          </VStack>

          <XText flex={1} fontSize='md'>
            {isSelectedAll ? 'ยกเลิกเลือกทั้งหมด' : 'เลือกทั้งหมด'}
          </XText>
        </HStack>
      </TouchableOpacity>
    )
  }

  _renderFooter = () => {
    const { mode, selectedWarehouseId = {} } = this.state
    const isSelectMode = mode === 'select'
    if (!isSelectMode) {
      return null
    }

    const selectedWhIds = Object.keys(selectedWarehouseId).filter((whId) => selectedWarehouseId[whId] === true)

    return (
      <HStack w='full' h='34px' p='2' bg='white' alignItems='center' space='1.5'>
        <XButton h='34px' w='80px' variant='ghost' onPress={this.closePicker}>
          ปิด
        </XButton>

        <XButton h='34px' flex={1} onPress={this._handleSubmitSelect} disabled={selectedWhIds.length === 0}>
          ยืนยัน
        </XButton>
      </HStack>
    )
  }

  _renderWarehouseItems = () => {
    const { warehouses } = this.state

    return warehouses.map(this._renderWarehouseItem)
  }

  _renderMain = () => {
    const { title } = this.props
    const { isVisible } = this.state

    return (
      <XOverlay
        // visible
        visible={isVisible}
        onRequestClose={this.closePicker}
      >
        <VStack w='full'>
          <XCustomHeader title={title || 'เลือกคลัง'} headerRightProps={{ closeIcon: true, onPressItem: this.closePicker }} />
          <XContent>
            {this._renderSelectAllItem()}
            {this._renderWarehouseItems()}
          </XContent>
          {this._renderFooter()}
        </VStack>
      </XOverlay>
    )
  }

  render() {
    const { isInitialized } = this.state

    if (!isInitialized) {
      return null
    }

    return this._renderMain()
  }
}

export default connect(
  (state) => ({
    selectedStore: getSelectedStore(state),
  }),
  (dispatch) => ({
    dispatch,
  }),
  null, // mergeProps (optional)
  { forwardRef: true } // Key option here
)(WarehousePickerBase)
// as React.FC<Omit<IWarehousePickerProps, 'selectedStore'>>
